import React, { useState, useEffect } from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { motion } from "framer-motion";
import styles from "./MyListPage.module.css";

import Navbar from "components/Navbar";
import BottomNav from "components/BottomNav";
import TextInput from "components/TextInput";
import Button from "Shared/Button";
import IconButton from "components/IconButton";
import add from "../../assets/add.svg";
import CategoryList from "components/CategoryList";
import SavedListPage from "pages/SavedListPage";
import { useTranslation } from "react-i18next";
import {
  useLists,
  useCreateList,
  useDeleteList,
  useUpdateList,
} from "../../hooks/useLists";
import { useCart, getCartItemCount } from "../../hooks/useCart";

export function MyListPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [newListName, setNewListName] = useState("");
  const [nameError, setNameError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [localCategory, setLocalCategory] = useState(null);
  const [activeTab, setActiveTab] = useState("My Lists");

  const listMatch = useRouteMatch("/my-lists/:listId");
  const urlListId = listMatch?.params?.listId ?? location?.state?.listId;

  const { data: listsData, isLoading } = useLists();
  const categories = listsData ?? [];
  // The query is idle until the contact id resolves, so isLoading alone is
  // false before any data has arrived.
  const listsLoaded = listsData !== undefined;
  const createList = useCreateList();
  const deleteList = useDeleteList();
  const updateList = useUpdateList();
  const { data: cartData } = useCart();
  const cartCount = getCartItemCount(cartData);

  // Any navigation (e.g. the "My Lists" nav link) drops a locally selected
  // list that has no id to put in the URL.
  useEffect(() => {
    setLocalCategory(null);
  }, [location.key]);

  const urlCategory =
    urlListId != null
      ? categories.find(
          (cat) => String(cat?.listId ?? cat?.setId) === String(urlListId)
        )
      : null;
  const selectedCategory = localCategory ?? urlCategory ?? null;
  const showBackButton = selectedCategory !== null;

  // Canonicalize state-based entries (e.g. back from stock details) to the
  // bookmarkable URL, and drop ids that do not match any list.
  useEffect(() => {
    if (urlListId == null || !listsLoaded) return;
    if (!urlCategory) {
      history.replace("/my-lists");
      return;
    }
    if (!listMatch) {
      history.replace({
        pathname: `/my-lists/${urlListId}`,
        state: location.state,
      });
    }
  }, [urlListId, urlCategory, listMatch, listsLoaded, history, location.state]);

  const handleSaveClick = () => {
    if (!newListName.trim()) {
      setNameError(t("Please enter a list name."));
      return;
    }
    // TODO: listTypeId hardcoded until BSM fixes uniqueness constraint to allow multiple lists of the same type
    createList.mutate({ name: newListName, listTypeId: 4 });
    setNewListName("");
    setNameError("");
    setIsEditing(false);
  };

  const handleDeleteClick = (listId) => {
    if (listId == null) return;
    deleteList.mutate(listId);
  };

  const handleCancelClick = () => {
    setNewListName("");
    setNameError("");
    setIsEditing(false);
  };

  const handleTitleClick = () => setIsEditing(true);

  const handleTabClick = (item) => {
    setActiveTab(item.label);
    history.push(item.href);
  };

  const handleChevronClick = (category) => {
    const listId = category?.listId ?? category?.setId;
    if (listId != null) {
      history.push(`/my-lists/${listId}`);
    } else {
      setLocalCategory(category);
    }
  };

  const handleListRename = (category) => {
    if (category?.isSystemList) return;
    const listId = category?.listId ?? category?.setId;
    const name = category?.name?.trim?.();
    if (!listId || !name) return;
    updateList.mutate({ listId, name });
  };

  const handleHeaderBackClick = () => {
    setLocalCategory(null);
    setNewListName("");
    setNameError("");
    setIsEditing(false);
    if (urlListId != null) history.push("/my-lists");
  };

  const menuItems = [
    { label: "WWG Store", href: "/wwg-store" },
    { label: "My Lists", href: "/my-lists" },
    { label: "My Orders", href: "/my-orders" },
    { label: "Cart", href: "/cart" },
  ];

  return (
    <div
      className={`${styles.container} ${
        selectedCategory ? styles.fillViewport : ""
      }`}
    >
      <div className={styles.desktopNav}>
        <Navbar
          onClick={handleHeaderBackClick}
          isHome={newListName === "" && selectedCategory === null}
          menuItems={menuItems}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          showBackButton={showBackButton}
          cartCount={cartCount}
        />
      </div>

      {selectedCategory ? (
        <div className={styles.savedList}>
          <SavedListPage
            listId={
              selectedCategory?.isNavigable === false
                ? null
                : selectedCategory.listId ?? selectedCategory.setId
            }
            listName={selectedCategory?.name}
            listType={selectedCategory?.type}
            onBack={handleHeaderBackClick}
          />
        </div>
      ) : (
        <div className={styles.listWrapper}>
          <div className={styles.pointer}>
            {isEditing ? (
              <div className={styles.createContainer}>
                <div className={styles.textInputContainer}>
                  <TextInput
                    label={t("New List")}
                    placeholder={t("Create New List")}
                    value={newListName}
                    setValue={(e) => {
                      setNewListName(e.target.value);
                      if (nameError) setNameError("");
                    }}
                  />
                  {nameError && (
                    <div className={styles.errorMessage} role="alert">
                      {nameError}
                    </div>
                  )}
                </div>
                <div className={styles.buttonsContainer}>
                  <Button
                    text={t("Cancel")}
                    onClick={handleCancelClick}
                    className="cancelBtn"
                  />
                  <Button
                    text={t("Save")}
                    onClick={handleSaveClick}
                    className="saveBtn"
                  />
                </div>
              </div>
            ) : (
              <div className={styles.title} onClick={handleTitleClick}>
                <IconButton icon={add} /> {t("NEW LIST")}
              </div>
            )}
          </div>

          <div className={styles.listClip}>
            <motion.div
              key={isLoading ? "loading" : "loaded"}
              className={styles.list}
              initial={{ opacity: 0, y: -40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, ease: "easeOut" }}
            >
              <CategoryList
                categories={categories}
                onDelete={handleDeleteClick}
                onChevronClick={handleChevronClick}
                onSave={handleListRename}
              />
            </motion.div>
          </div>
        </div>
      )}

      <BottomNav cartCount={cartCount} />
    </div>
  );
}

export default MyListPage;
